import React, { useEffect, useState } from 'react';
import { planService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import Table from '../components/Table';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Input from '../components/Input';
import { FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';

const Plans = () => {
  const { user } = useAuth();
  const currencySymbol = user?.resellerId?.currency?.symbol || 'RM';
  const currencyCode = user?.resellerId?.currency?.code || 'MYR';
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [formData, setFormData] = useState({
    planName: '',
    planCode: '',
    description: '',
    pricing: {
      monthly: 0,
      quarterly: 0,
      yearly: 0,
    },
    limits: {
      maxBranches: 1,
      maxUsers: 5,
      maxProducts: 1000,
      maxTransactionsPerMonth: -1,
    },
    features: [],
  });

  /* ── Grouped by customer-portal nav sections ── */
  const featureGroups = [
    {
      group: 'Portal Modules',
      items: [
        { value: 'basic_pos', label: 'POS & Sales', desc: 'POS, Invoices, Pre-Orders, Sales Returns' },
        { value: 'inventory_management', label: 'Products & Inventory', desc: 'Products, Brands, Categories, Inventory, Vendors, Pricebooks' },
        { value: 'customer_management', label: 'Customers', desc: 'Customer Records, Price Books, Ledger' },
        { value: 'employee_management', label: 'Staff Management', desc: 'Staff Accounts & Roles' },
        { value: 'online_ordering', label: 'Online Store', desc: 'Online Shop, Online Orders' },
        { value: 'reports', label: 'Reports', desc: 'Sales, Stock, Tax, Payment Reports' },
        { value: 'advanced_reports', label: 'Advanced Reports', desc: 'Profit & Loss, Customer Sales, Top Customers' },
      ],
    },
    {
      group: 'Add-ons',
      items: [
        { value: 'loyalty_program', label: 'Loyalty Program', desc: 'Customer loyalty points & rewards' },
        { value: 'custom_receipts', label: 'Custom Receipts', desc: 'Customizable receipt templates' },
        { value: 'delivery_management', label: 'Delivery', desc: 'Delivery tracking & management' },
        { value: 'multi_branch', label: 'Multi Branch', desc: 'Multiple branch locations' },
      ],
    },
  ];

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await planService.getAll();
      setPlans(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await planService.update(currentPlan._id, formData);
        toast.success('Plan updated successfully');
      } else {
        await planService.create(formData);
        toast.success('Plan created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (plan) => {
    setCurrentPlan(plan);
    setFormData({
      planName: plan.planName,
      planCode: plan.planCode,
      description: plan.description || '',
      pricing: plan.pricing,
      limits: plan.limits,
      features: plan.features || [],
    });
    setEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        await planService.delete(id);
        toast.success('Plan deleted successfully');
        fetchPlans();
      } catch (error) {
        toast.error('Failed to delete plan');
      }
    }
  };

  const handleFeatureToggle = (feature) => {
    setFormData((prev) => {
      const features = prev.features.includes(feature)
        ? prev.features.filter((f) => f !== feature)
        : [...prev.features, feature];
      return { ...prev, features };
    });
  };

  const resetForm = () => {
    setFormData({
      planName: '',
      planCode: '',
      description: '',
      pricing: {
        monthly: 0,
        quarterly: 0,
        yearly: 0,
      },
      limits: {
        maxBranches: 1,
        maxUsers: 5,
        maxProducts: 1000,
        maxTransactionsPerMonth: -1,
      },
      features: [],
    });
    setEditMode(false);
    setCurrentPlan(null);
  };

  const columns = [
    { header: 'Plan Name', accessor: 'planName' },
    { header: 'Code', accessor: 'planCode' },
    { header: 'Max Branches', render: (row) => row.limits.maxBranches },
    { header: 'Max Users', render: (row) => row.limits.maxUsers },
    { header: 'Monthly Price', render: (row) => `${currencySymbol}${row.pricing.monthly}` },
    { header: 'Features', render: (row) => row.features.length },
    {
      header: 'Status',
      render: (row) => (
        <span
          className={`px-2 py-1 text-xs rounded-full ${
            row.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleEdit(row)}
            className="text-purple-600 hover:text-purple-800"
          >
            <FiEdit size={18} />
          </button>
          <button
            onClick={() => handleDelete(row._id)}
            className="text-red-600 hover:text-red-800"
          >
            <FiTrash2 size={18} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Plans</h1>
        <Button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          <FiPlus className="inline mr-2" /> Create Plan
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : (
        <Table columns={columns} data={plans} />
      )}

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editMode ? 'Edit Plan' : 'Create New Plan'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Plan Name"
              name="planName"
              value={formData.planName}
              onChange={(e) => setFormData({ ...formData, planName: e.target.value })}
              required
            />
            <Input
              label="Plan Code"
              name="planCode"
              value={formData.planCode}
              onChange={(e) =>
                setFormData({ ...formData, planCode: e.target.value.toUpperCase() })
              }
              required
              disabled={editMode}
            />
          </div>

          <Input
            label="Description"
            name="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />

          <h3 className="text-lg font-semibold mt-6 mb-4">Pricing</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label={`Monthly (${currencySymbol})`}
              type="number"
              step="0.01"
              value={formData.pricing.monthly}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  pricing: { ...formData.pricing, monthly: parseFloat(e.target.value) },
                })
              }
              required
            />
            <Input
              label={`Quarterly (${currencySymbol})`}
              type="number"
              step="0.01"
              value={formData.pricing.quarterly}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  pricing: { ...formData.pricing, quarterly: parseFloat(e.target.value) },
                })
              }
            />
            <Input
              label={`Yearly (${currencySymbol})`}
              type="number"
              step="0.01"
              value={formData.pricing.yearly}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  pricing: { ...formData.pricing, yearly: parseFloat(e.target.value) },
                })
              }
            />
          </div>

          <h3 className="text-lg font-semibold mt-6 mb-4">Limits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Max Branches"
              type="number"
              value={formData.limits.maxBranches}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  limits: { ...formData.limits, maxBranches: parseInt(e.target.value) },
                })
              }
              required
            />
            <Input
              label="Max Users"
              type="number"
              value={formData.limits.maxUsers}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  limits: { ...formData.limits, maxUsers: parseInt(e.target.value) },
                })
              }
              required
            />
            <Input
              label="Max Products"
              type="number"
              value={formData.limits.maxProducts}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  limits: { ...formData.limits, maxProducts: parseInt(e.target.value) },
                })
              }
              required
            />
            <Input
              label="Max Transactions/Month (-1 = unlimited)"
              type="number"
              value={formData.limits.maxTransactionsPerMonth}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  limits: {
                    ...formData.limits,
                    maxTransactionsPerMonth: parseInt(e.target.value),
                  },
                })
              }
            />
          </div>

          <h3 className="text-lg font-semibold mt-6 mb-2">Features & Permissions</h3>
          <p className="text-xs text-gray-400 mb-3">These control which modules the merchant can access in their portal</p>
          {featureGroups.map((group) => (
            <div key={group.group} className="mb-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{group.group}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {group.items.map((feature) => {
                  const checked = formData.features.includes(feature.value);
                  return (
                    <label
                      key={feature.value}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-all select-none ${
                        checked ? 'border-purple-300 bg-purple-50/60' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleFeatureToggle(feature.value)}
                        className="w-3.5 h-3.5 accent-purple-600 rounded flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <span className={`text-sm font-medium ${checked ? 'text-purple-800' : 'text-gray-700'}`}>{feature.label}</span>
                        <p className="text-[10px] text-gray-400 leading-tight">{feature.desc}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="flex space-x-2 mt-6">
            <Button type="submit" className="flex-1">
              {editMode ? 'Update' : 'Create'} Plan
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Plans;
